const MIN_LENGTH = 200;
const BINARY_PLACEHOLDER = '[binary]';

const BINARY_FIELD_NAMES = new Set([
  'attachment',
  'attachments',
  'base64',
  'base64data',
  'binary',
  'binarydata',
  'blob',
  'buffer',
  'bytes',
  'contentbytes',
  'filecontent',
]);

const FILE_CONTENT_FIELD_NAMES = new Set(['content', 'data']);

function isWhitespace(code: number): boolean {
  return (
    (code >= 0x09 && code <= 0x0d) ||
    code === 0x20 ||
    code === 0xa0 ||
    code === 0x1680 ||
    (code >= 0x2000 && code <= 0x200a) ||
    code === 0x2028 ||
    code === 0x2029 ||
    code === 0x202f ||
    code === 0x205f ||
    code === 0x3000 ||
    code === 0xfeff
  );
}

function isBase64Character(code: number): boolean {
  return (
    (code >= 0x41 && code <= 0x5a) ||
    (code >= 0x61 && code <= 0x7a) ||
    (code >= 0x30 && code <= 0x39) ||
    code === 0x2b ||
    code === 0x2f
  );
}

function getDataUrlPayloadStart(value: string): number | null {
  if (value.length < 13 || value.slice(0, 5).toLowerCase() !== 'data:') {
    return null;
  }

  const commaIndex = value.indexOf(',', 5);
  if (
    commaIndex < 12 ||
    value.slice(commaIndex - 7, commaIndex).toLowerCase() !== ';base64'
  ) {
    return null;
  }

  return commaIndex + 1;
}

function isLongBase64String(value: string, startIndex = 0): boolean {
  let dataLength = 0;
  let paddingLength = 0;
  let sawPadding = false;

  for (let index = startIndex; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (isWhitespace(code)) continue;

    if (code === 0x3d) {
      sawPadding = true;
      paddingLength += 1;
      if (paddingLength > 2) return false;
      continue;
    }

    if (sawPadding || !isBase64Character(code)) return false;
    dataLength += 1;
  }

  const totalLength = dataLength + paddingLength;
  if (totalLength < MIN_LENGTH) return false;

  if (paddingLength === 0) {
    return dataLength % 4 !== 1;
  }

  return (
    totalLength % 4 === 0 &&
    ((paddingLength === 1 && dataLength % 4 === 3) ||
      (paddingLength === 2 && dataLength % 4 === 2))
  );
}

function isSerializedBuffer(
  value: unknown
): value is { type: 'Buffer'; data: number[] } {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { type?: string }).type === 'Buffer' &&
    Array.isArray((value as { data?: unknown }).data)
  );
}

function normalizeFieldName(fieldName?: string): string {
  return (fieldName ?? '').replace(/[-_\s]/g, '').toLowerCase();
}

function hasFileMetadata(value: Record<string, unknown>): boolean {
  const contentType = value.contentType ?? value.mimeType;
  if (typeof contentType !== 'string') return false;

  return (
    typeof value.size === 'number' ||
    typeof value.fileName === 'string' ||
    typeof value.filename === 'string' ||
    typeof value.name === 'string'
  );
}

function hasBinaryStringContext(
  fieldName: string | undefined,
  parentHasFileMetadata: boolean
): boolean {
  const normalized = normalizeFieldName(fieldName);
  return (
    BINARY_FIELD_NAMES.has(normalized) ||
    (parentHasFileMetadata && FILE_CONTENT_FIELD_NAMES.has(normalized))
  );
}

function isBinaryValue(
  value: unknown,
  fieldName?: string,
  parentHasFileMetadata = false
): boolean {
  if (value == null) return false;
  if (typeof value === 'string') {
    const dataUrlPayloadStart = getDataUrlPayloadStart(value);
    if (dataUrlPayloadStart !== null) {
      return isLongBase64String(value, dataUrlPayloadStart);
    }

    return (
      hasBinaryStringContext(fieldName, parentHasFileMetadata) &&
      isLongBase64String(value)
    );
  }
  if (isSerializedBuffer(value)) return true;
  if (typeof ArrayBuffer !== 'undefined' && value instanceof ArrayBuffer)
    return true;
  if (typeof Uint8Array !== 'undefined' && value instanceof Uint8Array)
    return true;
  return false;
}

export function isBinary(value: unknown, fieldName?: string): boolean {
  return isBinaryValue(value, fieldName);
}

export function getBinaryDisplaySummary(value: unknown): string | null {
  return isBinary(value) ? 'binary' : null;
}

function sanitizeValue(
  value: unknown,
  fieldName?: string,
  parentHasFileMetadata = false
): unknown {
  if (value == null) return value;
  if (isBinaryValue(value, fieldName, parentHasFileMetadata)) {
    return BINARY_PLACEHOLDER;
  }
  if (Array.isArray(value)) {
    return value.map((nestedValue) =>
      sanitizeValue(nestedValue, fieldName, parentHasFileMetadata)
    );
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const fileMetadata = hasFileMetadata(record);
    const out: Record<string, unknown> = {};
    for (const [key, nestedValue] of Object.entries(record)) {
      out[key] = sanitizeValue(nestedValue, key, fileMetadata);
    }
    return out;
  }
  return value;
}

export function sanitizeForDisplay(value: unknown): unknown {
  return sanitizeValue(value);
}

export function toDisplayString(value: unknown): string {
  const summary = getBinaryDisplaySummary(value);
  if (summary !== null) return summary;
  if (typeof value === 'object' && value !== null) {
    try {
      return JSON.stringify(sanitizeForDisplay(value), null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value ?? '');
}
